import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import IVenueSettings from '../../interfaces/IVenueSettings';
import { IProfileContainerChildren } from '../../container/ProfileContainer';
import ListLoading from '../util/Loading';
import ProfileForm from './ProfileForm';
import IDynamicFormRow from '../../yoello-lib/modules/dynamicForms/interfaces/IDynamicFormRow';
import { HTTPMethods } from '../../yoello-lib/modules/API/API';
import { Paper } from '@material-ui/core';
import IVenue from "../../yoello-lib/modules/API/interfaces/IVenue";
import useTask from "../../hooks/useTask";
import { useSelector } from 'react-redux';
import { IReduxAppState } from '../../reducers';
import { useAPIV2 } from '../../yoello-lib/modules/API2/useAPIv2';
import APIExtensions from '../../yoello-lib/modules/API2/APIDefs';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { IAPIReturnObject } from '../../yoello-lib/modules/API2/types';
import { isObjectEmpty } from '../../utils/objectCheck';
import { IFee } from '../../yoello-lib/modules/interfaces/IFee';
import { OrdersSettingContainer } from "../ordersSetting/OrdersSettingContainer"
import { IYoelloTheme } from '../../yoello-lib/modules/themes/IYoelloTheme';
import { useSnackbarMessages } from '../../yoello-lib/modules/components/snackbar/SnackbarContext';
import { useDeliveryFees } from '../ordersSetting/Forms/useDeliveryFees';
export interface IProfileForms {
    transformFunction?: (data: any) => any;
    title: string;
    formKey: string;
    formButtonText?: string;
    subTitle?: string;
    formRows: IDynamicFormRow[];
    resolver?: any;
    disable?:boolean;
}
interface ISettingsProfileComponentProps extends IProfileContainerChildren {
    profileForms: IProfileForms[];
    title?: string;
    isOrdersSettingPage?: boolean;
    isDisabled?: boolean;
    onFailedInteraction?: Function
}

/**Generic component for dealing with the settings API and multiple forms for settings */
const SettingsProfileComponent: FunctionComponent<ISettingsProfileComponentProps> = ({ title, venueId, isOrdersSettingPage, profileForms, isDisabled , onFailedInteraction }) => {
    const { increaseDoneTasks } = useTask();
    const { sendSnackbarMessage } = useSnackbarMessages()!;
    const { t } = useTranslation();
    const selectedVenueId = useSelector((state: IReduxAppState) => state.selectedVenueId);
    const {deliveryFees, onDeliveryFeesSubmit, isLoading } = useDeliveryFees();
    const { callAPI, apiData } = useAPIV2<IVenueSettings>(APIExtensions.venueSettings, {
        yoelloAPIParams: { venue_id: selectedVenueId },
        selectorKeyParams: { venue_id: selectedVenueId },
        successCall: t('snackbar.updateSuccess'),
        successCallMethods: [HTTPMethods.PUT]
    });
    const { apiData: venueData, callAPI: callVenues } = useAPIV2<IVenue>(APIExtensions.venues, {
        selectorKeyParams: { venue_id: selectedVenueId },
        query: selectedVenueId
    });
   
    const { apiData: venueFees, callAPI: callVenueFees } = useAPIV2<any>(APIExtensions.venueFees,
        {
            selectorKeyParams: { venue_id: selectedVenueId },
            yoelloAPIParams: { venue_id: selectedVenueId, include_disabled: true, include_hidden: true },
            dispatcherParams: [selectedVenueId!]
        });

    /**Venue data and venue settings combined */
    const [settings, setSettings] = useState<any | null>(null);


    const useStyles = makeStyles((theme: IYoelloTheme) =>
        createStyles({
            leftButtonCustomClassName: {
                justifyContent: "unset!important",
            },
        }),
    );

    const classes = useStyles();

    useEffect(() => {
        const fetchData = async () => {
            if (apiData && typeof apiData === "object" &&
                venueData && typeof venueData === "object" &&
                venueFees && Array.isArray(venueFees)) {
                setSettings({
                    ...parseSettingsObject({
                        ...venueData,
                        ...apiData,
                        delivery_fees: deliveryFees
                    })
                });
            }
        };

        fetchData();
        // eslint-disable-next-line
    }, [apiData, venueData, venueFees, deliveryFees]);

    function parseSettingsObject(data: any) {
        let service_charge_pence_amount = data?.service_charge_pence_amount;
        let service_charge_percentage_amount = data?.service_charge_percentage_amount;
        let delivery_charge = data?.delivery_charge;
        /**Checks for the new Fee model */
        if (venueFees.length > 0) {
            const serviceIndex = venueFees.findIndex((fee: IFee) => fee.fee_type === "SERVICE");
            const deliveryIndex = venueFees.findIndex((fee: IFee) => fee.fee_type === "DELIVERY");
            service_charge_pence_amount = venueFees[serviceIndex]?.fee_amount;
            service_charge_percentage_amount = venueFees[serviceIndex]?.fee_percentage;
            delivery_charge = venueFees[deliveryIndex]?.fee_amount;
        }
        return {
            ...data,
            service_charge_pence_amount,
            service_charge_percentage_amount,
            delivery_charge,
            table_layout: data?.table_layout ?? []
        };
    }
    const onSubmit = (data: any) => {
        if(data.delivery_fees){
            return new Promise(async (resolve , reject) => {
                const result = await onDeliveryFeesSubmit(data.delivery_fees);
                if(result){
                    // @ts-ignore
                    return resolve();
                }else{
                    return reject()
                }
            })
        }else{
        return new Promise(async (resolve , reject) => {
            const delivery_radius_km = data.delivery_radius_km && data.delivery_radius_km.length > 0 ? parseFloat(data.delivery_radius_km) : data.delivery_radius_km === '' ? null : undefined;
            if(data.order_only_enabled === false && data.order_and_pay_enabled === false){
                sendSnackbarMessage(t("errors.orderingOptions"), "error");
                return reject();
            }
            const requestData =
            {
                ...data,
                delivery_radius_km
            };
            /**
             * @TODO We should move this out of this component - hacky?
             */
            if (typeof data.url_website === "string" || typeof data.url_twitter === "string" || typeof data.url_facebook === "string" || typeof data.url_instagram === "string") {
                console.log("calling venues");
                await callVenues(HTTPMethods.PUT, {
                    body: {
                        url_website: data.url_website || '',
                        url_facebook: data.url_facebook || '',
                        url_instagram: data.url_instagram || '',
                        url_twitter: data.url_twitter || '',
                    },
                });
                delete requestData.url_website;
                delete requestData.url_facebook;
                delete requestData.url_instagram;
                delete requestData.url_twitter;
            }
            /**this is all part of the hack.
             * New apis do not like empty objects or wrong columns
             */
            if (isObjectEmpty({ ...requestData })) {
                //@ts-ignore
                return resolve();
            }
            /**
             * This handles the new venue fees model
             * The only keys that matter atm are
             * service_charge_pence_amount - service_charge_percentage_amount - delivery_charge
             */
            if (venueFees && venueFees.length > 0) {
                if (requestData?.service_charge_pence_amount !== undefined
                    && requestData?.service_charge_percentage_amount !== undefined) {
                    const serviceIndex = venueFees?.findIndex((fee: any) => fee.fee_type === "SERVICE");
                    const obj = venueFees[serviceIndex];
                    const newObj = {
                        ...obj,
                        fee_amount: requestData?.service_charge_pence_amount,
                        fee_percentage: requestData?.service_charge_percentage_amount
                    };
                    const result = await callVenueFees(HTTPMethods.PATCH, {
                        query: newObj.id,
                        body: newObj,
                        yoelloAPIParams: { venue_id: undefined, include_deleted: undefined }
                    });
                    if (result.isSuccessful) {
                        await callVenueFees(HTTPMethods.GET);
                        sendSnackbarMessage(t("snackbar.updateSuccess"), "success");
                        increaseDoneTasks();
                    }
                    /**This one gets returned as this form block only updates these two values */
                    //@ts-ignore
                    return resolve();
                } else if (requestData?.delivery_charge !== undefined) {
                    const deliveryIndex = venueFees?.findIndex((fee: any) => fee.fee_type === "DELIVERY");
                    const obj = venueFees[deliveryIndex];
                    const newObj = {
                        ...obj,
                        fee_amount: requestData?.delivery_charge,
                    };
                    console.log({ newObj });
                    const result = await callVenueFees(HTTPMethods.PATCH, {
                        query: newObj.id,
                        body: newObj,
                        yoelloAPIParams: { venue_id: undefined, include_deleted: undefined }
                    });
                    if (result.isSuccessful) {
                        await callVenueFees(HTTPMethods.GET);
                        increaseDoneTasks();
                    }
                }
            }
            callAPI(HTTPMethods.PUT, {
                body: requestData, query: settings.id,
                params: undefined, yoelloAPIParams: undefined
            }).then((result: IAPIReturnObject<IVenueSettings>) => {
                if (result.isSuccessful) {
                    increaseDoneTasks();
                }
                //@ts-ignore
                resolve();
            });
        });
    }

    };
    
    const mapForms = () => {
        if (!settings) { return null; }
        if (isOrdersSettingPage) {
            return <OrdersSettingContainer
                settings={settings}
                onFormSubmit={onSubmit}
            />
        }
        return profileForms.map((mappedValue: IProfileForms, index: number) => {
            return <Paper style={{ padding: '2rem', marginTop: '0.5rem' }}>
                <ProfileForm
                    isDisabled={isDisabled}
                    leftButtonCustomClassName={classes.leftButtonCustomClassName}
                    {...mappedValue}
                    formData={settings}
                    onFormSubmit={onSubmit}
                    key={`form_${index}`}
                    onFailedInteraction={onFailedInteraction}
                />
            </Paper>;
        });
    };
    return <div>
        {settings !== null && !isLoading ? <>
            {mapForms()}
        </> : <ListLoading />
        }

    </div>;
};

export default SettingsProfileComponent;
